// A waiting change to a running campaign (L-05, round 2; step 2.30). When the business gates changes to who a running
// campaign targets or its offer, the change waits (Campaign.pending) with an approve_campaign_change task, while the
// approved version keeps running. These show the before and after with Approve and Reject (decideCampaignChange):
// on the campaign page (CampaignChangeCard) and as the task's card in Approvals (CampaignChangeTask).
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { HumanTask, JsonObject } from "../../lib/types";
import { campaigns, decideCampaignChange, type Campaign } from "../../lib/campaigns";
import { changeRows, changeWhat } from "../../lib/campaignEditor";
import { useAsync } from "../../lib/useAsync";
import { Button, Card } from "../kit";
import { Loading, Notice } from "../ui";
import "./campaigns.css";

const byWhom = (pending: JsonObject | null | undefined) => (String(pending?.by ?? "").startsWith("human:") ? "Someone on your team" : "Ogma");

/** The before-and-after table and the two buttons. */
export function CampaignChangeBody({ campaign, crm, onDone }: { campaign: Campaign; crm: Crm; onDone: (approved: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rows = changeRows(campaign);
  const decide = async (d: "approve" | "reject") => {
    setBusy(true);
    setError(null);
    try {
      await decideCampaignChange(crm, campaign.id, d);
      onDone(d === "approve");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="campaign-change">
      <p className="small">
        {byWhom(campaign.pending)} suggests a change to <strong>{campaign.name}</strong>: {changeWhat(campaign.pending)}. It keeps running as it is until you decide.
      </p>
      {rows.length ? (
        <div className="table-wrap">
          <table className="stack campaign-change-table">
            <caption className="sr-only">Before and after</caption>
            <thead>
              <tr><th scope="col">What changes</th><th scope="col">Now</th><th scope="col">Suggested</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <th scope="row" data-label="What changes">{r.label}</th>
                  <td data-label="Now">{r.before || <span className="muted">Not set</span>}</td>
                  <td data-label="Suggested">{r.after || <span className="muted">Cleared</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted small">The suggestion matches what runs now.</p>
      )}
      <Notice text={error} tone="bad" />
      <div className="fold-actions">
        <Button variant="primary" small onClick={() => void decide("approve")} disabled={busy}>Approve</Button>
        <Button variant="secondary" small onClick={() => void decide("reject")} disabled={busy}>Reject</Button>
      </div>
    </div>
  );
}

/** On the campaign page: shown only while a change waits. */
export function CampaignChangeCard({ campaign, crm, onDone }: { campaign: Campaign; crm: Crm; onDone: (approved: boolean) => void }) {
  if (!campaign.id || !campaign.pending) return null;
  return (
    <Card id="change" level={2} title="A change waits for you" subtitle={`Suggested change: ${changeWhat(campaign.pending)}`} waiting defaultOpen>
      <CampaignChangeBody campaign={campaign} crm={crm} onDone={onDone} />
    </Card>
  );
}

/** Approvals' line for the task. */
export function campaignChangeSummary(t: HumanTask): string {
  const name = typeof t.payload.name === "string" && t.payload.name ? t.payload.name : "a running campaign";
  const f = Array.isArray(t.payload.fields) ? t.payload.fields : [];
  const what = changeWhat({ ...(f.includes("audience") ? { audience: {} } : {}), ...(f.includes("card") ? { card: {} } : {}) });
  return `Change to ${name}: ${what}`;
}

/** The approve_campaign_change task's card in Approvals. */
export function CampaignChangeTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const id = String(task.payload.play_id ?? task.entity_id ?? "");
  const list = useAsync(() => campaigns(crm), [crm, id]);
  if (list.loading && !list.data) return <Loading label="Loading the campaign" />;
  if (list.error) return <Notice text={list.error.message} tone="bad" />;
  const c = (list.data ?? []).find((x) => x.id === id);
  if (!c || !c.pending) {
    return (
      <p className="small">
        This change has already been decided. <Link to={`/campaigns/list?campaign=${encodeURIComponent(id)}`}>Open the campaign</Link>
      </p>
    );
  }
  return (
    <>
      <CampaignChangeBody campaign={c} crm={crm} onDone={() => onDone()} />
      <p className="small">
        <Link to={`/campaigns/list?campaign=${encodeURIComponent(c.id)}`}>Open the campaign</Link>
      </p>
    </>
  );
}
